import React, { useCallback, useMemo, useRef } from 'react';
import { Layout } from 'tea-component';
import CardLoading from '@op/components/CardLoading';
import FilterCommon, { useFilterCommon } from '@opm/components/FilterCommon';
import { useFilterFromShortId } from '@opm/utils/history';
import useIntls from '@opm/hooks/useIntls';
import MarketContentBody from '@opm/components/MarketContentBody';
import ScrollBottomList from '@opm/components/ScrollBottomList';
import { DBOM_POINT } from '@opm/operationLog/buriedPoint';
import { CreativeTrendsDetailsPoint } from '@opm/operationLog/pages';
import { useCreativeTrendDetails, useTopicTypeColorMap } from '../hooks';
import TrendInfo from './component/TrendInfo';
import RelatedCreativesSection from './component/RelatedCreativesSection';
import './index.scss';
import { TranslateConfigProvider } from '@opm/hooks/useTranslateConfig';
import { MARKETING_AI_TAG_COLOR_KEY } from '@opm/pages/CompetitorsAdCreatives/constants';
import { AI_TAGS_COLORS } from '@op/pages/community/video/components/TagCard';
import { history } from 'umi';
import { adCreativesProcessor } from '../processors';
import { useCTGameMap } from '@opm/utils';
const { Content } = Layout;

/**
 * Creative Trends 详情页
 *
 * 布局顺序（按 Figma 3883_72158）：
 *  1. Content.Header（标题 = topic + 返回按钮）
 *  2. FilterCommon（AD 系列筛选器：Game / DateRange / Country / Channel / Type ...）
 *  3. TrendInfo（信息卡：Game/Impressions/Creatives/Avg.Days Active + 类目 Tags + DoD）
 *  4. RelatedCreativesSection（相关素材 Card/Table 双视图）
 *
 * 数据流：
 *   URL shortid → useFilterFromShortId 拿到基础 filter（含 game_id + topic）
 *   → 合并 FilterCommon 的二级筛选 → 传给 TrendInfo / RelatedCreativesSection
 */
export default function CreativeTrendsDetails() {
  DBOM_POINT.useInitPage({ buriedPoint: CreativeTrendsDetailsPoint });
  const { intl, isEn } = useIntls();

  // ─── 从 URL 短链恢复基础 filter（game_id + topic + 时间窗等） ───
  const { filter: userDefaultValue, loading: shortIdLoading } = useFilterFromShortId();

  // 标题 topic：后端按多语言下发 topic（zh）/ topic_en（en），按当前语言取值
  const headerTitle = useMemo(() => {
    const topic = isEn
      ? userDefaultValue?.topic_en || userDefaultValue?.topic
      : userDefaultValue?.topic;
    return topic || intl('Creative Trend Details');
  }, [isEn, userDefaultValue, intl]);
  const wholePageRef = useRef<HTMLDivElement>(null);

  // ─── 详情页独立 FilterCommon（AD* keys） ───
  const {
    filter: detailsFilter,
    setFilter: setUserFilter,
    loading: filterLoading,
    ...filterProps
  } = useFilterCommon({
    keys: [
      'ADCountryRegion',
      'ADChannel',
      'ADType',
      'ADPlatform',
      'ADImpressions',
      'ADMultiDateRange',
      'ADActiveDays',
      'ADFirstSeenDateSelect',
      'ADLastSeenDateSelect',
    ],
  });

  const filter = useMemo(() => {
    if (!detailsFilter) return null;
    return {
      ...userDefaultValue,
      ...detailsFilter,
    };
  }, [detailsFilter, userDefaultValue]);

  const { data: trendData, loading: trendLoading } = useCreativeTrendDetails({
    filter,
  });

  // ─── 游戏元信息字典（cond.materials_config_games）：id → meta，下发给 TrendInfo ───
  const { gameMap } = useCTGameMap();

  // ─── Topic Type chip 配色映射（cond.creative_trends_config_topic_types）：L1.label_en → IAITagColor ───
  const { topicTypeColorMap } = useTopicTypeColorMap();

  /** cond 回调：从 materials_config_video_tags 构建颜色映射写入 sessionStorage
   *  与 Competitors 主页一致，TableView 渲染 AI Tags 用色依赖此 key */
  const handleCondConfig = useCallback((condData: any) => {
    const videoTags = condData?.materials_config_video_tags;
    if (!Array.isArray(videoTags) || videoTags.length === 0) return;
    try {
      const colorMap: any = {};
      videoTags.forEach((item: any, index: number) => {
        colorMap[item.label_en] = AI_TAGS_COLORS[index];
      });
      sessionStorage.setItem(
        MARKETING_AI_TAG_COLOR_KEY,
        JSON.stringify(colorMap),
      );
    } catch (e) {
      console.warn('Failed to save AI_TAG_COLOR:', e);
    }
  }, []);

  return (
    <TranslateConfigProvider processor={adCreativesProcessor}>
      <ScrollBottomList  >
        <Content className="dbom-ct-details" ref={wholePageRef}>
          <CardLoading loading={shortIdLoading}>
            <Content.Header
              title={headerTitle}
              showBackButton
              onBackButtonClick={() => {
                history.goBack();
              }}
            />
            <MarketContentBody
              loading={filterLoading}
              filterSlot={
                <FilterCommon
                  {...filterProps}
                  userDefaultValue={userDefaultValue}
                  loadingHeight={150}
                  useOriDefaultValue
                  realTimeMode
                  extrRequestKeys={['materials_config_video_tags']}
                  backCondConfig={handleCondConfig}
                  confirm={(value: any) => {
                    setUserFilter(value);
                  }}
                />
              }
            >
              <TrendInfo
                data={trendData}
                loading={!!trendLoading}
                filter={filter}
                gameMap={gameMap}
                topicTypeColorMap={topicTypeColorMap}
              />
              <RelatedCreativesSection filter={filter} />
            </MarketContentBody>
          </CardLoading>
        </Content>
      </ScrollBottomList>
    </TranslateConfigProvider>
  );
}
